// modules
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";

// redux
import { useAppSelector, useAppDispatch } from "app/hooks/hooks";
import {
	getGroupsList,
	groupsListSelector,
	getGroupPermissionList,
	groupPermissionListSelector,
	loadingGroupPermissionSelector,
	createUser,
	hasUnaurizedErrorSelector,
	usersDataLoadingSelector
} from "redux/userList/userSlice";

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// utils
import { validateEmail, validateFirstName, validateLastName, validateUsername } from "utils/Validations/fieldValidations";
import { formatPermissionList } from "utils/formatPermissionList";
import { checkForMultipleLimits } from "utils/Generic/generic";

// constants
import { MFA_STATE, NewUser, Routes, USER_STATE } from "constants/global";

type AdminData = {
	firstname: string;
	lastname: string;
	username: string;
	email: string;
};

export type RoleGroupItem = {
	id: string;
	value: string;
};

export type RoleGroup = {
	title?: string;
	values?: Array<RoleGroupItem>;
};

export type FieldErrors = {
	firstname?: boolean;
	lastname?: boolean;
	username?: boolean;
	email?: boolean;
	groups?: boolean;
};

export type CreateUserPayload = {
	username: string;
	email: string;
	first_name: string;
	last_name: string;
	mfa_enabled?: string;
	groups: Array<string>;
	tenant_name?: string;
	tenant_code?: string;
};

export function useNewUser() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	// for displaying selected permission from group only
	const [selectedRoles, setSelectedRoles] = useState<Array<string>>([]);
	const [selectedGroups, setSelectedGroups] = useState<Array<string>>([]);
	const [currentSelectedGroup, setCurrentSelectedGroup] = useState<{ id: string; value: string }>({ id: "", value: "" });
	const [adminData, setAdminData] = useState<AdminData>({
		firstname: "",
		lastname: "",
		username: "",
		email: ""
	});
	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
		firstname: false,
		lastname: false,
		username: false,
		email: false,
		groups: false
	});
	const [is2FAChecked, set2FAChecked] = useState<boolean>(true);
	const [hasMultipleLimitRoles, setHasMultipleLimitRoles] = useState<boolean>(false);
	const groupPermissionListData = useAppSelector(groupPermissionListSelector);
	const groupListData = useAppSelector(groupsListSelector);
	const { data: groupsList } = groupListData || {};
	const permissionsLoading = useAppSelector(loadingGroupPermissionSelector) as unknown as number;
	const hasUnaurizedError = useAppSelector(hasUnaurizedErrorSelector);
	const createUserInProgress = useAppSelector(usersDataLoadingSelector) === 1;

	const activeGroupsList = useMemo(() => {
		return groupsList ? groupsList?.filter(({ active, group_code }) => group_code !== NewUser.TENANT_GROUP && active === USER_STATE.enabled) : [];
	}, [groupsList, groupListData]);

	const allGroupsList = useMemo(() => {
		return groupsList || [];
	}, [groupsList, groupListData]);

	// Change handler for First/Last Name, Username, Email
	const handleAdminDataChange = useCallback(
		({ id, value }: { id: string; value: string }) => {
			const newData = {
				...adminData,
				[id]: value
			};
			setAdminData(newData);
		},
		[adminData, setAdminData]
	);

	// Change handler for checkbox
	const handleEnable2FA = useCallback(
		(checked: boolean) => {
			set2FAChecked(checked);
		},
		[set2FAChecked]
	);

	useEffect(() => {
		dispatch(getGroupPermissionList({}));
		dispatch(getGroupsList({}));
	}, []);

	const permissionList = useMemo(() => {
		if (groupPermissionListData?.data && groupPermissionListData?.data?.length > 0) {
			return formatPermissionList(groupPermissionListData?.data);
		}
		return [];
	}, [groupPermissionListData, formatPermissionList]);

	// Click handler for add another group button
	const handleAddAnotherRole = useCallback(() => {
		if (currentSelectedGroup?.id) {
			const group = allGroupsList?.find(({ group_code }) => group_code === currentSelectedGroup?.id);
			const permissions = group?.permissions || [];

			const { retroClaim, reimbEVApproval, reimbApproval, reimbNonCashApproval, reimbNonMemberApproval } = checkForMultipleLimits(
				[...selectedRoles, ...permissions],
				permissionList
			);

			if (retroClaim || reimbEVApproval || reimbApproval || reimbNonCashApproval || reimbNonMemberApproval) {
				setHasMultipleLimitRoles(true);
			} else {
				setHasMultipleLimitRoles(false);
			}

			setSelectedRoles([...selectedRoles, ...permissions]);
			setSelectedGroups([...selectedGroups, currentSelectedGroup?.id]);
			setCurrentSelectedGroup({ id: "", value: "" });
		}
	}, [
		allGroupsList,
		hasMultipleLimitRoles,
		currentSelectedGroup,
		selectedRoles,
		activeGroupsList,
		groupsList,
		selectedGroups,
		setSelectedGroups,
		setSelectedRoles,
		setCurrentSelectedGroup
	]);

	const handleRemoveSelectedGroup = useCallback(
		(value: string) => {
			// Remove selected group
			const updatedGroups = selectedGroups?.filter((currentGroup: string) => currentGroup?.toLowerCase() !== value?.toLowerCase());
			setSelectedGroups(updatedGroups);

			// Remove respective permissions
			const group = allGroupsList?.find(({ group_code }) => group_code === value);
			const permissions = group?.permissions || [];

			const currentPermissions = [...selectedRoles];

			permissions.forEach((permission) => {
				const index = currentPermissions.indexOf(permission);
				currentPermissions.splice(index, 1);
			});

			const { retroClaim, reimbEVApproval, reimbApproval, reimbNonCashApproval, reimbNonMemberApproval } = checkForMultipleLimits(currentPermissions, permissionList);

			if (retroClaim || reimbEVApproval || reimbApproval || reimbNonCashApproval || reimbNonMemberApproval) {
				setHasMultipleLimitRoles(true);
			} else {
				setHasMultipleLimitRoles(false);
			}
			setSelectedRoles(currentPermissions);
		},
		[activeGroupsList, groupsList, selectedGroups, selectedRoles, setSelectedGroups, setSelectedRoles]
	);

	// Check all required fields are entered
	const isCreateUserButtonEnabled = useMemo(() => {
		const { username, email } = adminData;

		if (username && email && selectedGroups?.length > 0 && !hasMultipleLimitRoles) {
			if (!fieldErrors.email && !fieldErrors.firstname && !fieldErrors.lastname && !fieldErrors.username) {
				return true;
			}
		}

		return false;
	}, [hasMultipleLimitRoles, adminData, selectedGroups, fieldErrors]);

	const onSuccess = useCallback(() => {
		navigate(Routes.ROLE_MANAGEMENT);
	}, [navigate]);

	const handleGoBack = useCallback(() => {
		navigate(Routes.ROLE_MANAGEMENT);
	}, [navigate]);

	const onFailure = useCallback(() => {
		navigate(`${Routes.ROLE_MANAGEMENT}/${Routes.CREATE_USER}`);
	}, [navigate]);

	const handleCurrentSelectedGroup = useCallback(
		(value: string, option: any) => {
			setCurrentSelectedGroup({ id: option?.id, value });
		},
		[setCurrentSelectedGroup]
	);

	const validateFields = useCallback(
		(submit: boolean) => {
			const fieldsToValidate = [
				{ key: "firstname", validator: validateFirstName, required: false },
				{ key: "lastname", validator: validateLastName, required: false },
				{ key: "username", validator: validateUsername, required: true },
				{ key: "email", validator: validateEmail, required: true }
			];

			setFieldErrors({
				firstname: false,
				lastname: false,
				username: false,
				email: false,
				groups: false
			});

			const errors: Array<{ field: string | undefined }> = [];

			fieldsToValidate.forEach((field: { key: string; validator: (args: string) => boolean; required: boolean }) => {
				const key = field.key as keyof AdminData;
				const value = adminData[key];
				if (value || (submit && field?.required)) {
					const isValid = field.validator(value);

					if (!isValid) {
						errors.push({ field: field.key });
					}
				}
			});

			if (!selectedGroups.length) {
				errors.push({ field: "groups" });
			}

			if (errors.length) {
				const errs: FieldErrors = {};
				errors.forEach((err) => {
					errs[err.field as keyof FieldErrors] = true;
				});
				setFieldErrors(errs);
				return;
			}

			return true;
		},
		[adminData, selectedGroups, setFieldErrors, validateFirstName, validateLastName, validateUsername, validateEmail]
	);

	const createUserSuccess = useCallback(
		(hasErrors: boolean, message: string | undefined, user_id = "") => {
			if (user_id && hasErrors && message === NewUser.NEW_USER_SUCCESS_MEESSAGE) {
				CountDownModal({
					timer: 3,
					closable: true,
					title: NewUser.USER_CREATED,
					subtitle: NewUser.USER_CREATED_SUBTITLE,
					okButton: {
						label: NewUser.USER_CREATED_OK_BTN,
						onClick: onSuccess
					},
					onTimeComplete: onSuccess
				});
			}
		},
		[CountDownModal, onSuccess]
	);

	const createUserFailure = useCallback(
		(errorObj: Array<{ message: string }>) => {
			if (
				errorObj?.length > 0 &&
				(errorObj[0]?.message?.includes(NewUser?.USER_ALREADY_EXISTS) ||
					errorObj[0]?.message?.includes(NewUser?.EMAIL_ALREADY_EXISTS) ||
					errorObj[0]?.message?.includes(NewUser?.USERNAME_ALREADY_EXISTS))
			) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: NewUser.NEW_USER_ALREADY_EXISTS,
					onTimeComplete: onFailure
				});
			}
		},
		[CountDownModal, onFailure]
	);

	const createPayload = useCallback(() => {
		const payload: CreateUserPayload = {
			username: adminData?.username,
			email: adminData?.email,
			first_name: adminData?.firstname,
			last_name: adminData?.lastname,
			mfa_enabled: is2FAChecked ? MFA_STATE.enabled : MFA_STATE.disabled,
			groups: selectedGroups
		};

		Object.keys(payload).forEach((prop) => {
			const key = prop as keyof CreateUserPayload;
			if (payload[key] === "") {
				delete payload[key];
			}
		});

		return payload;
	}, [adminData, is2FAChecked, selectedGroups]);

	// Proceed to create user only if all required fields are entered.
	const proceedToCreateUser = useCallback(async () => {
		const isFieldsValid = isCreateUserButtonEnabled && validateFields(true);

		if (isCreateUserButtonEnabled && isFieldsValid) {
			const payload = createPayload();
			try {
				const { data, errors, message } = await dispatch(createUser(payload)).unwrap();

				createUserSuccess(!errors?.length, message, data?.user_id);
			} catch (error) {
				// Added any as it is axios object
				const err = error as any;
				if (err?.response?.status === 401) {
					navigate(Routes.LOGIN, { state: { path: `${Routes?.ROLE_MANAGEMENT}/${Routes?.CREATE_USER}` } });
				}

				const errorObj = err?.response?.data?.errors;
				createUserFailure(errorObj);
			}
		}
	}, [isCreateUserButtonEnabled, is2FAChecked, selectedGroups, adminData, createUser, onSuccess, dispatch, validateFields]);

	useEffect(() => {
		if (hasUnaurizedError) {
			navigate(Routes.LOGIN, { state: { path: `${Routes?.ROLE_MANAGEMENT}/${Routes?.CREATE_USER}` } });
		}
	}, [hasUnaurizedError]);

	return {
		permissionList,
		adminData,
		is2FAChecked,
		groupsList: activeGroupsList,
		allGroupsList,
		selectedRoles,
		selectedGroups,
		currentSelectedGroup,
		isCreateUserButtonEnabled,
		loading: permissionsLoading === 1,
		fieldErrors,
		createUserInProgress,
		hasMultipleLimitRoles,
		createUserFailure,
		setSelectedRoles,
		setSelectedGroups,
		validateFields,
		onSuccess,
		handleGoBack,
		handleAddAnotherRole,
		setCurrentSelectedGroup: handleCurrentSelectedGroup,
		handleEnable2FA,
		handleAdminDataChange,
		handleRemoveSelectedGroup,
		proceedToCreateUser
	};
}
